import { useQuery } from '@tanstack/react-query';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { reportVortexStatus } from '../lib/api';

/**
 * Vortex Monitor — read-only view of "who's qualified for vortex". Two sections:
 *   - Queue: every vortex_slot entry, its position, and whether it has graduated
 *     or how many more entries must be minted before it does.
 *   - Watchlist: slots accumulating tokens toward the entry requirement.
 *
 * Modeled on reports.tsx. Pure reads — no mutations.
 */
export function VortexPage() {
  const q = useQuery({ queryKey: ['vortex-status'], queryFn: reportVortexStatus });

  const data = q.data;
  const graduatedCount = data?.queue.filter((r) => r.graduated).length ?? 0;
  const waitingCount = (data?.queue.length ?? 0) - graduatedCount;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Vortex Monitor</h1>
        <p className="text-sm text-muted-foreground">
          The Vortex queue (who has qualified) and slots accumulating tokens toward entry.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Token requirement" value={data ? fmt(data.tokenRequirement) : '—'} />
        <Stat label="Graduation N" value={data ? String(data.graduationRequirement) : '—'} />
        <Stat label="Total minted" value={data ? String(data.totalMinted) : '—'} />
        <Stat label="Graduated" value={String(graduatedCount)} />
        <Stat label="Waiting" value={String(waitingCount)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Queue — qualified entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !data?.queue.length ? (
            <p className="p-6 text-sm text-muted-foreground">No vortex entries yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium text-right">#</th>
                  <th className="px-4 py-2 font-medium">Slot</th>
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Membership</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Entered</th>
                </tr>
              </thead>
              <tbody>
                {data.queue.map((r) => (
                  <tr key={r.vortexId} className="border-b last:border-0">
                    <td className="px-4 py-2 text-right font-mono text-xs">{r.vortexId}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.slotCode}
                      {r.afterGraduate && (
                        <span className="ml-1 text-[10px] text-muted-foreground">(re-entry)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.fullName ?? r.email}
                      <span className="text-muted-foreground"> ({r.email})</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{r.membershipName}</td>
                    <td className="px-4 py-2 text-xs">
                      {r.graduated ? (
                        <Badge variant="success">Graduated</Badge>
                      ) : r.entriesRemaining === 0 ? (
                        <Badge>Eligible</Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          Waiting · needs {r.entriesRemaining} more
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Watchlist — accumulating tokens</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {q.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !data?.watchlist.length ? (
            <p className="p-6 text-sm text-muted-foreground">No slots are holding vortex tokens.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Slot</th>
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Membership</th>
                  <th className="px-4 py-2 font-medium text-right">Tokens</th>
                  <th className="px-4 py-2 font-medium">Progress to entry</th>
                </tr>
              </thead>
              <tbody>
                {data.watchlist.map((r) => (
                  <tr key={r.slotId} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{r.slotCode}</td>
                    <td className="px-4 py-2 text-xs">
                      {r.fullName ?? r.email}
                      <span className="text-muted-foreground"> ({r.email})</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{r.membershipName}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {fmt(r.availableToken)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${r.progressPct}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-muted-foreground">{r.progressPct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-lg font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/** Trim trailing zeros from a money/numeric string for compact display. */
function fmt(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 4 }) : v;
}
